import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { getStudios, getSites, createStudio, createSite, getOccupiers, assignOccupier, unassignOccupier, getStudioHistory } from '@/lib/api';
import { DoorOpen, Loader2, User, Plus, Building2, AlertCircle, UserPlus, ArrowRight, Search, Check, X, Eye, History, Calendar, FileText } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';

const statusColors = {
  occupied: 'bg-[#E8EDE5] text-[#3A4035]',
  vacant: 'bg-[#F2F0EB] text-[#5C5C55]',
  reserved: 'bg-[#F4EFE6] text-[#7A6849]',
};

export default function StudiosPage() {
  const [studios, setStudios] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCreateSite, setShowCreateSite] = useState(false);
  const [assignStudio, setAssignStudio] = useState(null);
  const [confirmUnassign, setConfirmUnassign] = useState(null);
  const [selectedStudio, setSelectedStudio] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const siteFilter = searchParams.get('site') || 'all';
  const currentSite = sites.find(s => s.site_id === siteFilter);

  const loadData = React.useCallback(() => {
    Promise.all([getStudios(siteFilter !== 'all' ? siteFilter : undefined), getSites()])
      .then(([s, st]) => { setStudios(s); setSites(st); }).catch(console.error).finally(() => setLoading(false));
  }, [siteFilter]);
  useEffect(() => { loadData(); }, [loadData]);

  const handleAddStudio = () => { sites.length === 0 ? setShowCreateSite(true) : setShowCreate(true); };

  const handleSelectStudio = async (studio) => {
    if (selectedStudio?.studio_id === studio.studio_id) { setSelectedStudio(null); setHistory([]); return; }
    setSelectedStudio(studio);
    setHistory([]);
    setHistoryLoading(true);
    try { setHistory(await getStudioHistory(studio.studio_id)); }
    catch (e) { console.error(e); }
    finally { setHistoryLoading(false); }
  };

  const handleUnassign = async () => {
    if (!confirmUnassign) return;
    try { await unassignOccupier(confirmUnassign.studio_id); setConfirmUnassign(null); setSelectedStudio(null); loadData(); } catch (e) { console.error(e); }
  };

  const handleChangeOccupier = (studio) => {
    if (studio.status === 'occupied') { setConfirmUnassign(studio); }
    else { setAssignStudio(studio); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="studios-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight">Studios</h1>
          <p className="text-sm text-[#5C5C55] mt-1">{currentSite ? `${studios.length} at ${currentSite.name}` : `${studios.length} across all sites`}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={siteFilter} onValueChange={v => setSearchParams(v === 'all' ? {} : { site: v })}>
            <SelectTrigger className="w-full sm:w-56 bg-white rounded-xl border-[#E5E2DC] h-10" data-testid="site-filter"><SelectValue placeholder="All sites" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sites</SelectItem>
              {sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={handleAddStudio} className="bg-[#3A4035] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#2B3027] shrink-0" data-testid="add-studio-button">
            <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Add Studio
          </Button>
        </div>
      </div>

      {sites.length === 0 && (
        <div className="bg-[#FDFAF0] rounded-2xl border border-[#EDE4C8] p-6 mb-6 flex items-start gap-4" data-testid="no-sites-guardrail">
          <div className="w-10 h-10 rounded-xl bg-[#F8F0D8] flex items-center justify-center shrink-0"><AlertCircle className="w-5 h-5 text-[#8A7230]" strokeWidth={1.5} /></div>
          <div>
            <h3 className="text-sm font-medium text-[#8A7230] mb-1">Create a site first</h3>
            <p className="text-xs text-[#8A7230]/70 mb-3">Studios belong to sites. Create at least one site before adding studios.</p>
            <Button onClick={() => setShowCreateSite(true)} className="bg-[#3A4035] text-white rounded-full px-4 py-2 text-xs hover:bg-[#2B3027]" data-testid="guardrail-add-site-button">
              <Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Add Site
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {studios.map((studio, i) => (
          <div key={studio.studio_id}
            className={`bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)] transition-all duration-300 animate-fade-in stagger-${(i % 6) + 1}`}
            data-testid={`studio-card-${studio.studio_id}`}>
            <button onClick={() => handleSelectStudio(studio)} className="w-full p-6 text-left">
              <div className="flex items-start justify-between mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#F2F0EB] flex items-center justify-center">
                  <DoorOpen className="w-5 h-5 text-[#5C5C55]" strokeWidth={1.5} />
                </div>
                <span className={`text-xs px-3 py-1 rounded-full font-semibold capitalize ${statusColors[studio.status] || 'bg-[#F2F0EB] text-[#5C5C55]'}`}>{studio.status}</span>
              </div>
              <h3 className="font-heading text-base font-medium text-[#1A1A1A] mb-1">{studio.name}</h3>
              <p className="text-xs text-[#8A8B85] mb-3">{studio.description}</p>
              <div className="flex items-center justify-between pt-3 border-t border-[#F2F0EB]">
                <span className="text-lg font-heading font-medium text-[#1A1A1A]">£{studio.monthly_rent?.toFixed(0)}<span className="text-xs text-[#8A8B85] font-body">/mo</span></span>
                {studio.occupier_name && <div className="flex items-center gap-1.5 text-xs text-[#5C5C55]"><User className="w-3.5 h-3.5" strokeWidth={1.5} />{studio.occupier_name}</div>}
                {!studio.occupier_name && studio.size_sqft > 0 && <span className="text-xs text-[#8A8B85]">{studio.size_sqft} sq ft</span>}
              </div>
            </button>
            {/* Studio actions */}
            <div className="px-6 pb-4 flex gap-2">
              {studio.status === 'vacant' && (
                <button onClick={() => setAssignStudio(studio)}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[#3A4035] text-white text-xs font-medium hover:bg-[#2B3027] transition-colors"
                  data-testid={`assign-occupier-${studio.studio_id}`}>
                  <UserPlus className="w-3.5 h-3.5" strokeWidth={1.5} />Assign Occupier
                </button>
              )}
              {studio.status === 'occupied' && (
                <>
                  <button onClick={() => navigate('/occupiers')}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border border-[#E5E2DC] text-xs font-medium text-[#5C5C55] hover:bg-[#FAF9F6] transition-colors"
                    data-testid={`view-occupier-${studio.studio_id}`}>
                    <Eye className="w-3.5 h-3.5" strokeWidth={1.5} />View Occupier
                  </button>
                  <button onClick={() => handleChangeOccupier(studio)}
                    className="flex items-center justify-center gap-1.5 py-2 px-3 rounded-xl border border-[#E5E2DC] text-xs font-medium text-[#5C5C55] hover:bg-[#FAF9F6] transition-colors"
                    data-testid={`change-occupier-${studio.studio_id}`}>
                    <UserPlus className="w-3.5 h-3.5" strokeWidth={1.5} />Change
                  </button>
                </>
              )}
              {studio.status === 'reserved' && (
                <button onClick={() => navigate('/enquiries')}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border border-[#E5E2DC] text-xs font-medium text-[#5C5C55] hover:bg-[#FAF9F6] transition-colors"
                  data-testid={`view-pipeline-${studio.studio_id}`}>
                  <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />View Pipeline
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* ── STUDIO DETAIL PANEL WITH HISTORY ── */}
      {selectedStudio && (
        <div className="mt-6 bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden animate-fade-in" data-testid="studio-detail-panel">
          <div className="p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="font-heading text-lg font-medium text-[#1A1A1A]">{selectedStudio.name}</h3>
                <p className="text-xs text-[#8A8B85]">{selectedStudio.description}{selectedStudio.size_sqft ? ` · ${selectedStudio.size_sqft} sq ft` : ''}</p>
              </div>
              <button onClick={() => { setSelectedStudio(null); setHistory([]); }} className="p-1 hover:bg-[#F2F0EB] rounded-lg" data-testid="close-detail-panel">
                <X className="w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
              </button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
              <div><p className="text-[10px] text-[#8A8B85] uppercase tracking-wider">Status</p><p className={`text-xs font-semibold capitalize mt-0.5 ${selectedStudio.status === 'occupied' ? 'text-[#3A4035]' : 'text-[#5C5C55]'}`}>{selectedStudio.status}</p></div>
              <div><p className="text-[10px] text-[#8A8B85] uppercase tracking-wider">Rent</p><p className="text-xs font-semibold text-[#1A1A1A] mt-0.5">£{selectedStudio.monthly_rent}/mo</p></div>
              {selectedStudio.occupier_name && <div><p className="text-[10px] text-[#8A8B85] uppercase tracking-wider">Occupier</p><p className="text-xs font-semibold text-[#1A1A1A] mt-0.5">{selectedStudio.occupier_name}</p></div>}
              {selectedStudio.floor && <div><p className="text-[10px] text-[#8A8B85] uppercase tracking-wider">Floor</p><p className="text-xs font-semibold text-[#1A1A1A] mt-0.5">{selectedStudio.floor}</p></div>}
            </div>

            {/* Occupier History */}
            <div className="border-t border-[#F2F0EB] pt-4">
              <div className="flex items-center gap-2 mb-3">
                <History className="w-3.5 h-3.5 text-[#8A8B85]" strokeWidth={1.5} />
                <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85]">Occupier History</p>
              </div>
              {historyLoading ? (
                <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-[#8A8B85]" /></div>
              ) : history.length > 0 ? (
                <div className="space-y-2">
                  {history.map(c => (
                    <div key={c.contract_id} className="flex items-center gap-3 py-2.5 px-3 rounded-xl bg-[#FAF9F6] border border-[#F2F0EB]" data-testid={`history-${c.contract_id}`}>
                      <div className="w-7 h-7 rounded-full bg-[#F2F0EB] flex items-center justify-center text-[10px] font-semibold text-[#5C5C55] shrink-0">
                        {(c.occupier_name || '?').charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-[#1A1A1A] truncate">{c.occupier_name || 'Unknown'}</p>
                        <div className="flex items-center gap-2 text-[10px] text-[#8A8B85]">
                          <span className="flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5" strokeWidth={1.5} />{c.start_date || '—'}</span>
                          <span>to</span>
                          <span>{c.end_date || 'present'}</span>
                        </div>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize shrink-0 ${
                        c.status === 'active' ? 'bg-[#E8EDE5] text-[#3A4035]' :
                        c.status === 'terminated' ? 'bg-[#F2F0EB] text-[#8A8B85]' :
                        c.status === 'contract_sent' ? 'bg-[#FDFAF0] text-[#8A7230]' :
                        'bg-[#F2F0EB] text-[#5C5C55]'
                      }`}>{c.status?.replace('_', ' ')}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-[#8A8B85] py-2">No history for this studio</p>
              )}
            </div>
          </div>
        </div>
      )}

      {studios.length === 0 && sites.length > 0 && (
        <div className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-[#F2F0EB] flex items-center justify-center mx-auto mb-3"><DoorOpen className="w-7 h-7 text-[#D8DBD3]" strokeWidth={1.5} /></div>
          <h3 className="font-heading text-base font-medium text-[#1A1A1A] mb-1">No studios {currentSite ? `at ${currentSite.name}` : 'found'}</h3>
          <p className="text-xs text-[#8A8B85] mb-4">Add your first studio to get started.</p>
          <Button onClick={() => setShowCreate(true)} className="bg-[#3A4035] text-white rounded-full px-5 py-2.5 text-sm hover:bg-[#2B3027]" data-testid="empty-add-studio-button">
            <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} />Add Studio
          </Button>
        </div>
      )}

      <CreateStudioModal open={showCreate} onClose={() => setShowCreate(false)} sites={sites} defaultSite={siteFilter !== 'all' ? siteFilter : ''}
        onCreated={() => { setShowCreate(false); loadData(); }} onNeedSite={() => { setShowCreate(false); setShowCreateSite(true); }} />
      <CreateSiteModal open={showCreateSite} onClose={() => setShowCreateSite(false)} onCreated={() => { setShowCreateSite(false); loadData(); }} />
      {assignStudio && <AssignOccupierModal open={!!assignStudio} onClose={() => setAssignStudio(null)} studio={assignStudio} onAssigned={() => { setAssignStudio(null); loadData(); }} />}

      {/* Confirm change occupier dialog */}
      {confirmUnassign && (
        <Dialog open={!!confirmUnassign} onOpenChange={() => setConfirmUnassign(null)}>
          <DialogContent className="sm:max-w-sm bg-white rounded-2xl border-[#E5E2DC]" data-testid="confirm-change-modal">
            <DialogHeader>
              <DialogTitle className="font-heading text-lg">Change Occupier</DialogTitle>
              <DialogDescription className="text-xs text-[#8A8B85]">
                {confirmUnassign.name} is currently occupied by <strong>{confirmUnassign.occupier_name}</strong>
              </DialogDescription>
            </DialogHeader>
            <div className="bg-[#FDFAF0] rounded-xl p-3 border border-[#EDE4C8] flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-[#8A7230] shrink-0" strokeWidth={1.5} />
              <p className="text-xs text-[#8A7230]">The current occupier's active contract will be terminated.</p>
            </div>
            <div className="space-y-2 pt-1">
              <button onClick={() => { const s = confirmUnassign; setConfirmUnassign(null); setAssignStudio(s); }}
                className="w-full flex items-center gap-3 p-4 rounded-2xl border border-[#E5E2DC] hover:bg-[#F0F2EF] transition-colors text-left"
                data-testid="replace-occupier-btn">
                <div className="w-8 h-8 rounded-xl bg-[#E8EDE5] flex items-center justify-center"><UserPlus className="w-4 h-4 text-[#3A4035]" strokeWidth={1.5} /></div>
                <div><p className="text-sm font-medium text-[#1A1A1A]">Replace with new occupier</p><p className="text-[11px] text-[#8A8B85]">Assign a different practitioner</p></div>
              </button>
              <button onClick={handleUnassign}
                className="w-full flex items-center gap-3 p-4 rounded-2xl border border-[#F0D4D4] hover:bg-[#FDF2F2] transition-colors text-left"
                data-testid="remove-occupier-btn">
                <div className="w-8 h-8 rounded-xl bg-[#FDF2F2] flex items-center justify-center"><X className="w-4 h-4 text-[#B33A3A]" strokeWidth={1.5} /></div>
                <div><p className="text-sm font-medium text-[#1A1A1A]">Remove occupier</p><p className="text-[11px] text-[#8A8B85]">Mark studio as vacant</p></div>
              </button>
            </div>
            <Button variant="outline" onClick={() => setConfirmUnassign(null)} className="w-full rounded-full h-9 border-[#E5E2DC] text-xs mt-1">Cancel</Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// ── Assign Occupier Modal ──
function AssignOccupierModal({ open, onClose, studio, onAssigned }) {
  const [mode, setMode] = useState('link');
  const [occupiers, setOccupiers] = useState([]);
  const [loadingOcc, setLoadingOcc] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [startDate, setStartDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [rent, setRent] = useState(studio?.monthly_rent?.toString() || '');
  const [contractStatus, setContractStatus] = useState('active');
  const [accessStatus, setAccessStatus] = useState('pending');
  const [newForm, setNewForm] = useState({ full_name: '', email: '', phone: '', home_address: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const setNew = (k, v) => { setNewForm(f => ({ ...f, [k]: v })); setError(''); };

  useEffect(() => {
    if (open && mode === 'link') {
      setLoadingOcc(true);
      getOccupiers().then(setOccupiers).catch(console.error).finally(() => setLoadingOcc(false));
    }
  }, [open, mode]);

  useEffect(() => {
    if (open) { setSelectedUser(''); setSearch(''); setError(''); setSaving(false); setNewForm({ full_name: '', email: '', phone: '', home_address: '' }); setRent(studio?.monthly_rent?.toString() || ''); }
  }, [open, studio]);

  const filteredOcc = occupiers.filter(o => {
    const q = search.toLowerCase();
    return !q || o.name?.toLowerCase().includes(q) || o.email?.toLowerCase().includes(q) || o.business_name?.toLowerCase().includes(q);
  });

  const handleAssign = async () => {
    setSaving(true);
    setError('');
    try {
      const data = {
        mode, start_date: startDate, monthly_rent: parseFloat(rent) || studio.monthly_rent,
        contract_status: contractStatus, access_status: accessStatus,
      };
      if (mode === 'link') {
        if (!selectedUser) { setError('Select an occupier'); setSaving(false); return; }
        data.user_id = selectedUser;
      } else {
        if (!newForm.full_name.trim() || !newForm.email.trim()) { setError('Name and email required'); setSaving(false); return; }
        data.full_name = newForm.full_name.trim();
        data.email = newForm.email.trim();
        data.phone = newForm.phone.trim();
        data.home_address = newForm.home_address;
      }
      await assignOccupier(studio.studio_id, data);
      onAssigned();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Assignment failed. Please try again.');
    } finally { setSaving(false); }
  };

  const isOccupied = studio?.status === 'occupied';

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-lg bg-white rounded-2xl border-[#E5E2DC] max-h-[90vh] overflow-y-auto" data-testid="assign-occupier-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">{isOccupied ? 'Change' : 'Assign'} Occupier — {studio.name}</DialogTitle>
          <DialogDescription className="text-xs text-[#8A8B85]">
            {isOccupied ? `Currently occupied by ${studio.occupier_name}. Assigning a new occupier will replace them.` : 'Assign a practitioner to this studio.'}
          </DialogDescription>
        </DialogHeader>

        {isOccupied && (
          <div className="bg-[#FDFAF0] rounded-xl p-3 border border-[#EDE4C8] flex items-center gap-2" data-testid="replace-warning">
            <AlertCircle className="w-4 h-4 text-[#8A7230] shrink-0" strokeWidth={1.5} />
            <p className="text-xs text-[#8A7230]">Current occupier <strong>{studio.occupier_name}</strong> will be replaced and their contract terminated.</p>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 p-3 bg-[#FDF2F2] rounded-xl border border-[#F0D4D4]" data-testid="assign-error">
            <AlertCircle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} /><p className="text-xs text-[#B33A3A]">{error}</p>
          </div>
        )}

        {/* Mode tabs */}
        <div className="flex gap-2 bg-[#F2F0EB] rounded-xl p-1">
          <button onClick={() => setMode('link')} className={`flex-1 text-xs font-medium py-2 rounded-lg transition-colors ${mode === 'link' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#8A8B85]'}`} data-testid="mode-link">Link Existing</button>
          <button onClick={() => setMode('create')} className={`flex-1 text-xs font-medium py-2 rounded-lg transition-colors ${mode === 'create' ? 'bg-white text-[#1A1A1A] shadow-sm' : 'text-[#8A8B85]'}`} data-testid="mode-create">Create New</button>
        </div>

        {mode === 'link' && (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8B85]" strokeWidth={1.5} />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search occupiers..." className="pl-10 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="occupier-search" />
            </div>
            <div className="max-h-48 overflow-y-auto space-y-1 border border-[#F2F0EB] rounded-xl p-2" data-testid="occupier-list">
              {loadingOcc && <div className="py-4 text-center"><Loader2 className="w-4 h-4 animate-spin mx-auto text-[#8A8B85]" /></div>}
              {!loadingOcc && filteredOcc.length === 0 && <p className="text-xs text-[#8A8B85] py-4 text-center">No occupiers found</p>}
              {filteredOcc.map(o => (
                <button key={o.user_id} onClick={() => setSelectedUser(o.user_id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors ${selectedUser === o.user_id ? 'bg-[#E8EDE5] border border-[#D0D9CB]' : 'hover:bg-[#FAF9F6]'}`}
                  data-testid={`occupier-option-${o.user_id}`}>
                  <div className="w-8 h-8 rounded-full bg-[#F2F0EB] flex items-center justify-center text-xs font-semibold text-[#5C5C55] shrink-0">{o.name?.charAt(0)}</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#1A1A1A] truncate">{o.name}</p>
                    <p className="text-[10px] text-[#8A8B85] truncate">{o.business_name || o.email}</p>
                  </div>
                  {selectedUser === o.user_id && <Check className="w-4 h-4 text-[#3A4035] shrink-0" strokeWidth={1.5} />}
                </button>
              ))}
            </div>
          </div>
        )}

        {mode === 'create' && (
          <div className="space-y-3">
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Full Name <span className="text-[#B33A3A]">*</span></Label>
              <Input value={newForm.full_name} onChange={e => setNew('full_name', e.target.value)} placeholder="Full name" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="new-occ-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Email <span className="text-[#B33A3A]">*</span></Label>
                <Input value={newForm.email} onChange={e => setNew('email', e.target.value)} placeholder="email@example.com" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="new-occ-email" />
              </div>
              <div>
                <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Phone</Label>
                <Input value={newForm.phone} onChange={e => setNew('phone', e.target.value)} placeholder="07700 900000" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="new-occ-phone" />
              </div>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Home Address</Label>
              <Input value={newForm.home_address} onChange={e => setNew('home_address', e.target.value)} placeholder="Full address" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="new-occ-address" />
            </div>
          </div>
        )}

        {/* Assignment options */}
        <div className="space-y-3 pt-1 border-t border-[#F2F0EB]">
          <p className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#8A8B85] pt-2">Assignment Details</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Start Date</Label>
              <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="assign-start-date" />
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Monthly Rent (£)</Label>
              <Input type="number" value={rent} onChange={e => setRent(e.target.value)} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="assign-rent" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Contract</Label>
              <Select value={contractStatus} onValueChange={setContractStatus}>
                <SelectTrigger className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="assign-contract-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_signature">Pending Signature</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Access Key</Label>
              <Select value={accessStatus} onValueChange={setAccessStatus}>
                <SelectTrigger className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="assign-access-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <Button onClick={handleAssign} disabled={saving || (mode === 'link' && !selectedUser) || (mode === 'create' && (!newForm.full_name.trim() || !newForm.email.trim()))}
          className={`w-full rounded-full h-11 text-sm font-medium ${saving ? '' : 'bg-[#3A4035] text-white hover:bg-[#2B3027]'}`}
          data-testid="confirm-assign-btn">
          {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Assigning...</> : <><UserPlus className="w-4 h-4 mr-2" strokeWidth={1.5} />{isOccupied ? 'Replace & Assign' : 'Assign Occupier'}</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

// ── Create Studio Modal (unchanged logic) ──
function CreateStudioModal({ open, onClose, sites, defaultSite, onCreated, onNeedSite }) {
  const [form, setForm] = useState({ site_id: defaultSite || '', name: '', floor: '', monthly_rent: '', description: '', size_sqft: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setError(''); };
  useEffect(() => { if (defaultSite) set('site_id', defaultSite); }, [defaultSite]);
  useEffect(() => { if (open) { setForm({ site_id: defaultSite || '', name: '', floor: '', monthly_rent: '', description: '', size_sqft: '' }); setError(''); setSaving(false); } }, [open, defaultSite]);
  const canSubmit = form.site_id && form.name.trim() && form.monthly_rent;
  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!canSubmit) return;
    setSaving(true); setError('');
    try { await createStudio({ ...form, name: form.name.trim(), monthly_rent: parseFloat(form.monthly_rent) || 0, size_sqft: parseInt(form.size_sqft) || 0 }); onCreated(); }
    catch (err) { setError(err.response?.data?.detail || 'Failed to create studio.'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border-[#E5E2DC]" data-testid="create-studio-modal">
        <DialogHeader><DialogTitle className="font-heading text-lg">Add Studio</DialogTitle><DialogDescription className="text-xs text-[#8A8B85]">Add a new studio to a site</DialogDescription></DialogHeader>
        {error && <div className="flex items-center gap-2 p-3 bg-[#FDF2F2] rounded-xl border border-[#F0D4D4]"><AlertCircle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} /><p className="text-xs text-[#B33A3A]">{typeof error === 'string' ? error : 'Error'}</p></div>}
        {sites.length === 0 ? (
          <div className="bg-[#FDFAF0] rounded-xl p-4 border border-[#EDE4C8]" data-testid="no-sites-warning"><p className="text-sm font-medium text-[#8A7230] mb-1">No sites found</p><p className="text-xs text-[#8A7230]/70 mb-3">Create a site before adding studios.</p>
            <Button onClick={() => { onClose(); if (onNeedSite) onNeedSite(); }} className="bg-[#3A4035] text-white rounded-full px-4 py-2 text-xs hover:bg-[#2B3027]" data-testid="create-site-from-studio"><Plus className="w-3.5 h-3.5 mr-1.5" strokeWidth={1.5} />Add Site First</Button></div>
        ) : (
          <form onSubmit={handleSave} className="space-y-4 pt-1">
            <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Site <span className="text-[#B33A3A]">*</span></Label>
              <Select value={form.site_id} onValueChange={v => set('site_id', v)}><SelectTrigger className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="studio-site-select"><SelectValue placeholder="Select site" /></SelectTrigger><SelectContent>{sites.map(s => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Name <span className="text-[#B33A3A]">*</span></Label><Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Studio 8" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="studio-name-input" /></div>
              <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Floor</Label><Input value={form.floor} onChange={e => set('floor', e.target.value)} placeholder="Ground Floor" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="studio-floor-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Rent (£) <span className="text-[#B33A3A]">*</span></Label><Input type="number" value={form.monthly_rent} onChange={e => set('monthly_rent', e.target.value)} placeholder="650" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="studio-rent-input" /></div>
              <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Size (sq ft)</Label><Input type="number" value={form.size_sqft} onChange={e => set('size_sqft', e.target.value)} placeholder="150" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" data-testid="studio-size-input" /></div>
            </div>
            <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Description</Label><Textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description" rows={2} className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl resize-none" data-testid="studio-description-input" /></div>
            <Button type="submit" disabled={saving || !canSubmit} className={`w-full rounded-full h-11 text-sm font-medium ${canSubmit && !saving ? 'bg-[#3A4035] text-white hover:bg-[#2B3027]' : 'bg-[#E5E2DC] text-[#8A8B85] cursor-not-allowed'}`} data-testid="save-studio-button">
              {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Creating...</> : <><Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Add Studio</>}</Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Create Site Modal (fallback, same as Sites page) ──
function CreateSiteModal({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', address: '', city: '', postcode: '', description: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({});
  useEffect(() => { if (open) { setForm({ name: '', address: '', city: '', postcode: '', description: '' }); setSaving(false); setError(''); setTouched({}); } }, [open]);
  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setTouched(t => ({ ...t, [k]: true })); setError(''); };
  const canSubmit = form.name.trim() && form.address.trim();
  const handleSave = async (e) => {
    if (e) e.preventDefault(); setTouched({ name: true, address: true }); if (!canSubmit) return;
    setSaving(true); setError('');
    try { await createSite({ ...form, total_studios: 0 }); onCreated(); }
    catch (err) { setError(err.response?.data?.detail || 'Failed to create site.'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md bg-white rounded-2xl border-[#E5E2DC]" data-testid="create-site-fallback-modal">
        <DialogHeader><DialogTitle className="font-heading text-lg">Add Site</DialogTitle><DialogDescription className="text-xs text-[#8A8B85]">Create a site first, then add studios</DialogDescription></DialogHeader>
        {error && <div className="flex items-center gap-2 p-3 bg-[#FDF2F2] rounded-xl border border-[#F0D4D4]"><AlertCircle className="w-4 h-4 text-[#B33A3A] shrink-0" strokeWidth={1.5} /><p className="text-xs text-[#B33A3A]">{error}</p></div>}
        <form onSubmit={handleSave} className="space-y-4 pt-1">
          <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Site Name <span className="text-[#B33A3A]">*</span></Label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. The Crescent Studios" className={`mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10 ${touched.name && !form.name.trim() ? 'border-[#B33A3A]' : ''}`} data-testid="fallback-site-name" autoFocus /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Address <span className="text-[#B33A3A]">*</span></Label>
              <Input value={form.address} onChange={e => set('address', e.target.value)} placeholder="14 The Crescent" className={`mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10 ${touched.address && !form.address.trim() ? 'border-[#B33A3A]' : ''}`} data-testid="fallback-site-address" /></div>
            <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">City</Label><Input value={form.city} onChange={e => set('city', e.target.value)} placeholder="Manchester" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" /></div>
          </div>
          <div><Label className="text-[10px] tracking-[0.1em] uppercase font-semibold text-[#5C5C55]">Postcode</Label><Input value={form.postcode} onChange={e => set('postcode', e.target.value)} placeholder="M1 4BT" className="mt-1.5 bg-[#F2F0EB] border-transparent rounded-xl h-10" /></div>
          <Button type="submit" disabled={saving || !canSubmit} className={`w-full rounded-full h-11 text-sm font-medium ${canSubmit && !saving ? 'bg-[#3A4035] text-white hover:bg-[#2B3027]' : 'bg-[#E5E2DC] text-[#8A8B85] cursor-not-allowed'}`} data-testid="fallback-save-site">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Creating...</> : <><Plus className="w-4 h-4 mr-2" strokeWidth={1.5} />Add Site</>}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
